import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// GET /node_definitions — the list read. Backend: routes/node_definitions.js GET via
// modules/generic_functions/node_definitions/getNodeDefinitions.js. The full response is
// `{ nodes, pagination, config, filtersConfig }`.
//
// The list row is a LIGHTER projection than the single-GET record (no parameters / config / child_nodes) and is assembled by
// a different backend query — so `NodeDefinitionRow` is its own type, not `NodeDefinition`. We validate only the identifier
// fields the FE consumes (the async dropdown) plus `pagination.total_records`; extra list columns the backend sends are
// ignored. The endpoint supports `search` (ILIKE on name), `zone_name` (equals), `node_definition_id` (id equals — resolves
// a key back to its label), and `limit`/`offset` pagination.
export interface NodeDefinitionRow {
  readonly node_definition_id: number;
  readonly name: string;
  readonly zone_name: string;
}

export interface NodeDefinitionsQuery {
  readonly search?: string;
  readonly limit: number;
  readonly offset?: number;
  readonly zone_name?: string;
  readonly node_definition_id?: number;
}

export interface NodeDefinitionsListResponse {
  readonly nodes: readonly NodeDefinitionRow[];
  readonly pagination: { readonly total_records: number };
}

const rowSchema = object<NodeDefinitionRow>({
  node_definition_id: num(),
  name: str(),
  zone_name: str(),
});

const responseSchema = object<NodeDefinitionsListResponse>({
  nodes: array(rowSchema),
  pagination: object<{ readonly total_records: number }>({ total_records: num() }),
});

export const getNodeDefinitions = async (params: NodeDefinitionsQuery): Promise<NodeDefinitionsListResponse> => {
  const res = await api.get('/node_definitions', { params });
  const parsed = runSchema(responseSchema, res.data, 'getNodeDefinitions');
  if (!parsed.ok) {
    throw new Error('getNodeDefinitions: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['nodes', 'pagination.total_records']);
};
